import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {isNoneAdvisoryCurrency} from '@stubhub/currency-utils';
import {retrieveCurrencyExchangeRates, getLocalStorageExchangeRates} from '@stubhub/react-currency-exchange';
import StoreConfigModal from '@stubhub/react-store-config-modal';
import {getSupportedCurrencies} from '@stubhub/react-store-default-currency';
import {formatURLDomain} from '@stubhub/sh-url-utils';

import {getLanSelector} from './helpers/locale-selectors';

const LocalizationPicker = ({
  selectedCurrency,
  defaultCurrencyCodes = [],
  visible,
  onClose,
  onListItemClick,
  showLanguageSelector,
  showCurrencySelector,
  updateSelectedCurrency,
  updateExchangeRate,
  supportedCurrencies,
  updateSupportedCurrencies,
  defaultLocale,
  currentLocale,
  supportedLocales,
  isAdvisoryEnabled,
}) => {
  const prevDefaultCurrencieCodes = useRef(defaultCurrencyCodes?.length || 0);
  const prevSelectedCurrency = useRef(null);
  const requestedExchanges = useRef([]);
  const [currentCurrencyCode, setCurrentCurrencyCode] = useState(selectedCurrency);
  const lanSelector = getLanSelector(supportedLocales, currentLocale);
  const {items: languages} = lanSelector;

  const updateCurrency = useCallback(
    async (currencyCode) => {
      const isNoneCurrency = isNoneAdvisoryCurrency(currencyCode);
      typeof updateSelectedCurrency === 'function' && updateSelectedCurrency(currencyCode);

      if (currencyCode && !isNoneCurrency) {
        const innerdefaultCurrencyCodes = defaultCurrencyCodes
          .filter(
            (indexedCurrencyCode) =>
              indexedCurrencyCode !== currencyCode &&
              !requestedExchanges.current.includes(`${indexedCurrencyCode}_${currencyCode}`)
          )
          .filter(Boolean);
        if (innerdefaultCurrencyCodes.length) {
          innerdefaultCurrencyCodes.forEach((i) => {
            requestedExchanges.current.push(`${i}_${currencyCode}`);
          });
          await retrieveCurrencyExchangeRates(innerdefaultCurrencyCodes, currencyCode);
        }
        typeof updateExchangeRate === 'function' && updateExchangeRate(getLocalStorageExchangeRates());
      }
    },
    [defaultCurrencyCodes, updateExchangeRate, updateSelectedCurrency]
  );

  useEffect(() => {
    const loadSupportedCurrencies = async () => {
      const supportedCurrencies = await getSupportedCurrencies();
      updateSupportedCurrencies && updateSupportedCurrencies(supportedCurrencies);
    };

    if (isAdvisoryEnabled) {
      loadSupportedCurrencies();
    }
  }, [isAdvisoryEnabled, updateSupportedCurrencies]);

  useEffect(() => {
    if (isAdvisoryEnabled && supportedCurrencies?.length && prevSelectedCurrency.current !== selectedCurrency) {
      prevSelectedCurrency.current = selectedCurrency;
      setCurrentCurrencyCode(selectedCurrency);
      updateCurrency(selectedCurrency);
    }
  }, [isAdvisoryEnabled, selectedCurrency, updateCurrency, supportedCurrencies]);

  useEffect(() => {
    if (isAdvisoryEnabled && defaultCurrencyCodes?.length !== prevDefaultCurrencieCodes.current) {
      prevDefaultCurrencieCodes.current = defaultCurrencyCodes.length;
      updateCurrency(currentCurrencyCode);
    }
  }, [isAdvisoryEnabled, defaultCurrencyCodes, updateCurrency, currentCurrencyCode]);

  const handleLanguageChange = (key) => {
    const href = formatURLDomain({
      href: window.location.href,
      locale: key,
      defaultLocale,
      currentLocale,
    });
    window.location.href = href;
  };

  const onStoreConfigModalSubmit = (data) => {
    const {currency, locale} = data;

    if (showCurrencySelector) {
      const hasChanged = currentCurrencyCode !== currency;

      if (hasChanged) {
        onListItemClick && onListItemClick('currency')(currency);
        setCurrentCurrencyCode(currency);
        updateCurrency(currency);
      }
    }

    if (showLanguageSelector && locale && currentLocale !== locale) {
      onListItemClick && onListItemClick('language')(locale);
      handleLanguageChange(locale);
    }

    onClose && onClose();
  };

  return (
    <StoreConfigModal
      locales={showLanguageSelector && Array.isArray(languages) ? languages : []}
      currencies={showCurrencySelector && Array.isArray(supportedCurrencies) ? supportedCurrencies : []}
      currentLocale={currentLocale}
      currentCurrency={currentCurrencyCode}
      visible={visible}
      onClose={onClose}
      onSubmit={onStoreConfigModalSubmit}
    />
  );
};

LocalizationPicker.propTypes = {
  /** Current selected currency  */
  selectedCurrency: PropTypes.string,

  /** Currency codes that need to have exchange rate */
  defaultCurrencyCodes: PropTypes.arrayOf(PropTypes.string),

  /** If the modal should be visible or not */
  visible: PropTypes.bool,

  /** Function to invoke when the modal is closed */
  onClose: PropTypes.func,

  /** Function to invoke when  a value has changed*/
  onListItemClick: PropTypes.func,

  /** If the language selector should be shown or not   */
  showLanguageSelector: PropTypes.bool,

  /** If the currency selector and disclaimer should be shown or not */
  showCurrencySelector: PropTypes.bool,

  /** Callback function to change currently selected currency */
  updateSelectedCurrency: PropTypes.func,

  /** Function invoked when new exchange rate is retrieved */
  updateExchangeRate: PropTypes.func,

  /** List of supported currencies */
  supportedCurrencies: PropTypes.arrayOf(PropTypes.object),

  /** Function invoked when supported currencies are retrieved */
  updateSupportedCurrencies: PropTypes.func,

  /** Current default locale from gsConfig */
  defaultLocale: PropTypes.string,

  /** Current user locale */
  currentLocale: PropTypes.string,

  /** Array of supported locales for the site */
  supportedLocales: PropTypes.array,

  /** If advisory is enabled or not */
  isAdvisoryEnabled: PropTypes.bool,
};

export default LocalizationPicker;
